import React from "react";
import { useTranslation } from "react-i18next";
import { ChartColumnIncreasing } from "lucide-react";
import { cn } from "@/lib/utils";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  OPERATIONS_METRIC_CHART_Y_MAX,
  OPERATIONS_METRIC_WIDGET_HEIGHT,
} from "./constants";
import { OperationsStackedAreaChart } from "./OperationsStackedAreaChart";
import type { OperationsChartDataPoint, OperationsChartSeries } from "./types";

interface OperationsMetricChartWidgetProps {
  title: string;
  data: OperationsChartDataPoint[];
  series: OperationsChartSeries[];
  stacked?: boolean;
  yAxisMax?: number;
  yAxisTicks?: number[];
  yAxisLabel?: string;
  xAxisLabel?: string;
  formatValue?: (value: number) => string;
  formatYAxisTick?: (value: number) => string;
  showTooltipTotal?: boolean;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
  className?: string;
}

export const OperationsMetricChartWidget: React.FC<
  OperationsMetricChartWidgetProps
> = ({
  title,
  data,
  series,
  stacked,
  yAxisMax = OPERATIONS_METRIC_CHART_Y_MAX,
  yAxisTicks,
  yAxisLabel,
  xAxisLabel,
  formatValue,
  formatYAxisTick,
  showTooltipTotal,
  onExport,
  exportDisabled,
  className,
}) => {
  const { t } = useTranslation();
  const resolvedYAxisLabel =
    yAxisLabel ?? t("analytics.operations.axis.requests", "Requests");
  const resolvedXAxisLabel = xAxisLabel ?? t("analytics.common.date", "Date");

  return (
    <RevenueChartCard
      title={title}
      icon={<ChartColumnIncreasing />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      onExport={onExport}
      exportDisabled={exportDisabled}
      showExport={Boolean(onExport)}
      className={cn("flex flex-col", className)}
      style={{
        height: OPERATIONS_METRIC_WIDGET_HEIGHT,
        minHeight: OPERATIONS_METRIC_WIDGET_HEIGHT,
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <OperationsStackedAreaChart
          title={title}
          data={data}
          series={series}
          stacked={stacked}
          showLegend
          embedded
          fillHeight
          yAxisMax={yAxisMax}
          yAxisTicks={yAxisTicks}
          yAxisLabel={resolvedYAxisLabel}
          xAxisLabel={resolvedXAxisLabel}
          formatValue={formatValue}
          formatYAxisTick={formatYAxisTick}
          showTooltipTotal={showTooltipTotal}
        />
      </div>
    </RevenueChartCard>
  );
};
